import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { translate } from '~l10n/client';

import { SmallButton } from '~shared/components/buttons';
import { IconChevron } from '~shared/components/icons/standard';
import { SocrativeLogo } from '~shared/components/SocrativeLogo';
import { Spinner } from '~shared/components/Spinner';
import { toast } from '~shared/components/Toast';
import { UpdateEmailDialog } from '~teacher/components/Profile/ChangeEmail/UpdateEmailDialog';
import {
  RouterProvider,
  getTeacherRouter,
  useSocrativeRouter,
} from '~teacher/context/RouterContext';
import { useSendVerificationEmail } from '~teacher/hook/account';
import { useVerificationData } from '~teacher/hook/account/useVerificationData';
import { useLogout } from '~teacher/hook/auth';
import { queryClient } from '~teacher/queryClient';

import { isAxiosError } from '../../api/isAxiosError';

const ConfirmEmailPage = () => {
  const verificationStatusQuery = useVerificationData({
    select: (data) => data.newEmail ?? data.email,
  });

  if (!verificationStatusQuery?.data) {
    return <ConfirmEmailPageLoading />;
  }

  return <ConfirmEmailPageData email={verificationStatusQuery.data} />;
};

function ConfirmEmailPageLoading() {
  return (
    <div className="grid place-items-center">
      <div className="flex flex-col items-center justify-center min-h-screen gap-32 max-w-xl font-sans">
        <SocrativeLogo />
        <Spinner />
      </div>
    </div>
  );
}

function ConfirmEmailPageData({ email }: { email: string }) {
  const teacherRouter = useSocrativeRouter();
  const sendVerificationCode = useSendVerificationEmail();
  const logoutMutation = useLogout();
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [, setIsUpdateEmailDialogOpen] = UpdateEmailDialog.useIsOpen();

  const handleConfirmEmail = () => {
    setIsSendingVerification(true);
    sendVerificationCode.mutate(null, {
      onSuccess: () => {
        setIsSendingVerification(false);
        toast.positive(translate('Verification email sent'));
        teacherRouter.navigate('email-verify', { trigger: true });
      },
      onError: (error: unknown) => {
        setIsSendingVerification(false);
        if (isAxiosError(error)) {
          if (error.response?.status === 429) {
            toast(
              translate(
                'Too many requests. Please wait a while and try again later.'
              )
            );
            return;
          }
        }
        toast.critical(
          translate(
            'An error occurred. Please check your connection and try again.'
          )
        );
      },
    });
  };

  return (
    <div className="grid place-items-center">
      <div className="flex flex-col items-center justify-center min-h-screen gap-8 max-w-3xl font-sans">
        <SmallButton
          appearance="text"
          onClick={() => {
            logoutMutation.mutate(null, {
              onSuccess: () => {
                window.location.href = '/login/teacher/';
              },
              onError: (error: unknown) => {
                if (__DEV__) {
                  console.error(error);
                }
                toast.critical(
                  translate(
                    'An unknown error occurred. Please try again later.'
                  )
                );
              },
            });
          }}
        >
          <IconChevron direction="left" />
          {translate('Back to Login')}
        </SmallButton>

        <SocrativeLogo />

        <span className="text-md font-display leading-loose font-500 leading-normal text-blue-900">
          {translate('Confirm Email')}
        </span>

        <div className="flex flex-col items-center">
          <p className="text-base leading-loose text-blue-900 text-center leading-normal">
            {translate('Verification is required to secure your account.')}
          </p>

          <p className="text-base leading-loose text-blue-900 text-center leading-normal">
            {translate('A code will be sent to:')} <strong>{email}</strong>
          </p>
        </div>

        <div className="flex flex-col items-center">
          <p className="text-base leading-loose font-700 text-blue-900 text-center leading-normal">
            {translate('Can’t access this email?')}
          </p>

          <p
            className="text-base leading-loose text-blue-900 text-center leading-normal"
            onClick={() => setIsUpdateEmailDialogOpen(true)}
            dangerouslySetInnerHTML={{
              __html: translate(
                '<span class="font-700 text-cyan-700 cursor-pointer hover:underline">Change your email now</span> to receive verification code'
              ),
            }}
          ></p>
        </div>

        <div className="flex flex-col items-center gap-4 mt-4">
          <SmallButton
            appearance="primary"
            isLoading={isSendingVerification}
            onClick={handleConfirmEmail}
          >
            {translate('Confirm Email')}
          </SmallButton>
        </div>
        <UpdateEmailDialog />
      </div>
    </div>
  );
}

export default function doConfirmEmailPage() {
  const mainContentDiv = document.getElementById('main-content');
  if (!mainContentDiv) {
    throw new Error('Main content portal not found');
  }
  const teacherRouter = getTeacherRouter();

  ReactDOM.render(
    <RouterProvider router={teacherRouter}>
      <QueryClientProvider client={queryClient}>
        <ConfirmEmailPage />
        <ReactQueryDevtools />
      </QueryClientProvider>
    </RouterProvider>,
    mainContentDiv
  );
}
